import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { cancelHireProposal as cancelHireProposalApi, getHireProposal } from '../services/chatApi';
import UserStreamManager from '../utils/UserStreamManager';
import { useCurrentChat } from './CurrentChatContext';
import { useMediaViewer } from './MediaViewerContext';

/**
 * HireProposalContext — the chat's ONE pending hire proposal slot on the frontend
 * (agent_docs/employee_initiated_hire_prd.md §2, §4; the HumanComputerContext.pendingBatch pattern).
 *
 *   bootstrap  the chat GET carries `pending_hire_proposal` → seeded on chat change;
 *   live       WS HIRE_PROPOSAL_PENDING / HIRE_PROPOSAL_CLEARED (chat-scoped by chat_id);
 *   recovery   the chat meta says a proposal exists but we hold none → refetch after a beat, and
 *              on every stream reconciliation (reconnect / tab visible again);
 *   slot rule  a new pending command batch takes the slot (the backend also clears + publishes
 *              CLEARED; we mirror it locally so the panel never shows both);
 *   owner-only the proposal is the owner's to act on: a shared-employee viewer never fetches,
 *              never subscribes for a chat it does not own, and sees nothing.
 *
 * `proposal` carries the server shape plus `chat_id` (the chat it belongs to) so consumers can
 * guard against a stale slot from another chat, exactly like pendingBatch.chat_id.
 */
const HireProposalContext = createContext(null);

export const useHireProposal = () => {
  const context = useContext(HireProposalContext);
  if (!context) {
    throw new Error('useHireProposal must be used within HireProposalProvider');
  }
  return context;
};

export const HireProposalProvider = ({ children }) => {
  // Defensive consume (same as HumanComputerProvider): mounted globally, the CurrentChatContext
  // default is undefined outside a chat route.
  const chatContext = useCurrentChat();
  const currentChat = chatContext?.currentChat;
  const isOwner = !!chatContext?.isOwner;
  // The chat whose slot we track — null for non-owners, so nothing below runs for them.
  const chatId = isOwner && currentChat?.id ? currentChat.id : null;
  const bootId = currentChat?.pending_hire_proposal?.proposal_id || null;
  const {
    openHireProposal,
    closeHireProposal,
    toggleHireProposalFullscreen,
    rightPanelContent,
    mode,
  } = useMediaViewer();

  const [proposal, setProposal] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  // The owner closed the panel (✕ / Esc / Cancel) — don't auto-reopen for the same slot.
  const userClosedRef = useRef(false);
  const chatIdRef = useRef(chatId);
  const prevChatIdRef = useRef(null);
  const bootRef = useRef(currentChat?.pending_hire_proposal || null);
  const rightPanelContentRef = useRef(rightPanelContent);
  // A command batch holds the slot while human_computer_turn is true (PRD §0 ruling 3): a proposal
  // must not surface on top of it, whatever path delivers it (seed, WS, refetch).
  const hcTurnRef = useRef(!!currentChat?.human_computer_turn);
  useEffect(() => { hcTurnRef.current = !!currentChat?.human_computer_turn; }, [currentChat?.human_computer_turn]);
  useEffect(() => { chatIdRef.current = chatId; }, [chatId]);
  useEffect(() => { bootRef.current = currentChat?.pending_hire_proposal || null; }, [currentChat?.pending_hire_proposal]);
  useEffect(() => { rightPanelContentRef.current = rightPanelContent; }, [rightPanelContent]);

  const isPanelOpen = rightPanelContent === 'hire_proposal';
  const isFullscreen = isPanelOpen && mode === 'fullscreen';

  // Show a proposal: store it (tagged with its chat) and auto-open the panel unless the owner
  // closed it for this slot, or a pending command batch currently holds the slot (a batch wins).
  const showProposal = useCallback((p, forChatId) => {
    if (!p || hcTurnRef.current) return;
    setProposal({ ...p, chat_id: forChatId });
    if (!userClosedRef.current && rightPanelContentRef.current !== 'hc_review') {
      openHireProposal();
    }
  }, [openHireProposal]);

  const clearLocal = useCallback(() => {
    setProposal(null);
    closeHireProposal();
  }, [closeHireProposal]);

  // Authoritative refetch (recovery path). Stale-response guard: drop a reply for a chat we left.
  const fetchProposal = useCallback(async () => {
    if (!chatId) return;
    const requestChat = chatId;
    try {
      const { data } = await getHireProposal(requestChat);
      if (chatIdRef.current !== requestChat) return;
      if (data?.proposal) {
        showProposal(data.proposal, requestChat);
      } else {
        clearLocal();
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to fetch hire proposal:', error);
      }
    }
  }, [chatId, showProposal, clearLocal]);

  // Bootstrap: on chat change reset the slot and seed from the chat meta; when the meta later
  // names a NEW proposal id, seed that too. The meta going quiet never clears a live proposal
  // (status broadcasts may omit the field) — CLEARED / the refetch own clearing.
  useEffect(() => {
    if (!chatId) {
      prevChatIdRef.current = null;
      userClosedRef.current = false;
      setProposal(null);
      closeHireProposal();
      return;
    }
    if (prevChatIdRef.current !== chatId) {
      prevChatIdRef.current = chatId;
      userClosedRef.current = false;
      setProposal(null);
    }
    const boot = bootRef.current;
    if (boot && boot.proposal_id === bootId) {
      showProposal(boot, chatId);
    }
  }, [chatId, bootId, showProposal, closeHireProposal]);

  // Stale-state recovery: the chat meta says a proposal is pending but we hold none → we missed
  // the WS event or the seed was dropped. Refetch after a short debounce (the legitimate
  // "just loaded" window resolves through the bootstrap above).
  useEffect(() => {
    if (!chatId || !bootId) return undefined;
    if (proposal) return undefined;
    const timer = setTimeout(() => {
      if (!proposal) {
        console.warn('[HireProposal] Recovering: chat meta names a proposal but none is held, re-fetching');
        fetchProposal();
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [chatId, bootId, proposal, fetchProposal]);

  // Reconnect / tab-visible reconciliation: a CLEARED or PENDING may have been missed while away.
  useEffect(() => {
    if (!chatId) return undefined;
    const unsubscribe = UserStreamManager.addReconciliationCallback(() => { fetchProposal(); });
    return () => unsubscribe();
  }, [chatId, fetchProposal]);

  // WS: a new proposal for this chat (replaces any previous one — the newest wins).
  useEffect(() => {
    if (!chatId) return undefined;
    const unsubscribe = UserStreamManager.subscribe('HIRE_PROPOSAL_PENDING', (data) => {
      if (data?.chat_id !== chatId) return;
      if (data.proposal) {
        // A fresh proposal re-arms auto-open even if the owner closed the previous one.
        userClosedRef.current = false;
        showProposal(data.proposal, chatId);
      } else {
        fetchProposal();
      }
    });
    return () => unsubscribe();
  }, [chatId, showProposal, fetchProposal]);

  // Ref mirror of the held proposal id for the CLEARED handler below (no resubscribe churn).
  const proposalIdRef = useRef(null);
  useEffect(() => { proposalIdRef.current = proposal?.proposal_id || null; }, [proposal?.proposal_id]);

  // WS: the slot was cleared (hired / cancelled / replaced / superseded).
  useEffect(() => {
    if (!chatId) return undefined;
    const unsubscribe = UserStreamManager.subscribe('HIRE_PROPOSAL_CLEARED', (data) => {
      if (data?.chat_id !== chatId) return;
      setProposal((prev) => {
        // A CLEARED for an older proposal must not wipe a newer one that already replaced it.
        if (prev && data.proposal_id && prev.proposal_id !== data.proposal_id) return prev;
        return null;
      });
      if (!data.proposal_id || proposalIdRef.current === null || proposalIdRef.current === data.proposal_id) {
        closeHireProposal();
      }
    });
    return () => unsubscribe();
  }, [chatId, closeHireProposal]);

  // Slot rule (PRD §0 ruling 3): a new pending command batch takes the slot. The backend clears the
  // proposal and publishes CLEARED; mirror it locally so the two never render together.
  useEffect(() => {
    if (!chatId) return undefined;
    const unsubscribe = UserStreamManager.subscribe('HUMAN_COMPUTER_COMMANDS_PENDING', (data) => {
      if (data?.chat_id !== chatId) return;
      const hasPending = data.batch?.commands?.some((c) => c.status === 'pending');
      if (hasPending) clearLocal();
    });
    return () => unsubscribe();
  }, [chatId, clearLocal]);
  useEffect(() => {
    if (chatId && currentChat?.human_computer_turn && proposal) {
      clearLocal();
    }
  }, [chatId, currentChat?.human_computer_turn, proposal, clearLocal]);

  // ==================== Actions ====================

  const openPanel = useCallback(() => {
    userClosedRef.current = false;
    openHireProposal();
  }, [openHireProposal]);

  // Cancel = the owner's DELETE: clears the slot for good (fire and forget, silent to the proposer).
  const cancelProposal = useCallback(async () => {
    if (!chatId) return;
    setCancelling(true);
    try {
      await cancelHireProposalApi(chatId);
    } catch (error) {
      // A 404 means the slot was already gone (hired / replaced) — same end state.
      if (error.response?.status !== 404) {
        console.error('Failed to cancel hire proposal:', error);
      }
    } finally {
      setCancelling(false);
      userClosedRef.current = true;
      clearLocal();
    }
  }, [chatId, clearLocal]);

  // ✕ / Esc = Cancel (Nitish, 2026-09-11: "if user decline or close the panel ... it's just gone
  // forever", exactly like pending commands). The build's alternative (close keeps the proposal,
  // chiclet as the way back) is one line: `userClosedRef.current = true; closeHireProposal();`.
  const closePanel = useCallback(() => {
    if (proposal) {
      cancelProposal();
    } else {
      userClosedRef.current = true;
      closeHireProposal();
    }
  }, [proposal, cancelProposal, closeHireProposal]);

  // After a successful Hire the backend clears the slot (and publishes CLEARED); drop it locally now.
  const clearProposal = useCallback(() => {
    userClosedRef.current = true;
    clearLocal();
  }, [clearLocal]);

  const value = {
    proposal,
    isPanelOpen,
    isFullscreen,
    cancelling,
    openPanel,
    closePanel,
    toggleFullscreen: toggleHireProposalFullscreen,
    cancelProposal,
    clearProposal,
    refreshProposal: fetchProposal,
  };

  return (
    <HireProposalContext.Provider value={value}>
      {children}
    </HireProposalContext.Provider>
  );
};

export default HireProposalContext;
